import { RefObject, useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { AuditLogsAccessResponse, AuditLogsService, PublicAuditLog } from 'api';
import { useCurrentProductFromRoute } from 'hooks';
import { ALL_PRODUCTS, BUSINESS_UNIT_QUERY_PARAMS } from 'pages/AuditLogs/constants';
import { FlatOption, ProductSelection, QueryParams } from 'pages/AuditLogs/types';
import { getIdFromBusinessEntitySelection } from 'pages/AuditLogs/utilities';
import { Logger } from 'utilities';

const PAGE_SIZE = 50;

const useAuditLogs = (userId: number | null, tableRef: RefObject<HTMLDivElement | null>) => {
  const product = useCurrentProductFromRoute();
  const [logsScope, setLogsScope] = useState<null | AuditLogsAccessResponse>(null);
  const [logs, setLogs] = useState<Array<PublicAuditLog>>([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [queryParams, setQueryParams] = useState<QueryParams | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const getLogsScope = useCallback(async () => {
    try {
      const response = await AuditLogsService.apiAuditLogsAccessRead();
      setLogsScope(response);
      setHasAccess(true);
    } catch (error: any) {
      if (error?.status === 403) {
        enqueueSnackbar('You do not have permission to access audit logs.', { variant: 'info' });
      }
    }
  }, [enqueueSnackbar]);

  const loadLogsPage = useCallback(
    async ({ page, append = false, queryParams }: { page: number; append: boolean; queryParams?: QueryParams }) => {
      try {
        setIsRequesting(true);
        const requestParameters = queryParams || new Map<string, string>();
        const response = await AuditLogsService.apiAuditLogsList(
          page,
          PAGE_SIZE,
          requestParameters.get('domain_area') as PublicAuditLog.domain_area,
          requestParameters.get('root_entity_id') ? Number(requestParameters.get('root_entity_id')) : undefined,
          requestParameters.get('root_entity_type') as PublicAuditLog.root_entity_type,
          requestParameters.get('company_measurement_date_id')
            ? Number(requestParameters.get('company_measurement_date_id'))
            : undefined,
          requestParameters.get('company') ? Number(requestParameters.get('company')) : undefined,
          requestParameters.get('firm') ? Number(requestParameters.get('firm')) : undefined,
          requestParameters.get('fund') ? Number(requestParameters.get('fund')) : undefined,
          requestParameters.get('start_date') as string | undefined,
          requestParameters.get('end_date') as string | undefined,
          requestParameters.get('user') ? Number(requestParameters.get('user')) : undefined,
          requestParameters.get('product') as ProductSelection
        );
        setLogs(prevLogs => (append ? [...prevLogs, ...response.results] : response.results));
        setHasNextPage(!!response.next);
        if (append) {
          setCurrentPage(page);
        }
      } catch (error) {
        Logger.error(`Failed to load audit logs page ${page}:`, error);
      } finally {
        setIsRequesting(false);
      }
    },
    []
  );

  const handleBusinessEntitySelection = useCallback((selection: FlatOption | null) => {
    // This function would need cascade to an effect that triggers a new query
    const kindToQueryParamMap: Record<string, string> = {
      firm: 'firm',
      fund: 'fund',
      company: 'company',
      standalone: 'company',
    };
    if (selection) {
      const queryParamKey = kindToQueryParamMap[selection.kind];
      setQueryParams(prevState => {
        const newState = new Map(prevState);
        // we don't allow multi select for now, so delete previous query params if any
        BUSINESS_UNIT_QUERY_PARAMS.forEach(param => newState.delete(param));
        newState.set(queryParamKey, getIdFromBusinessEntitySelection(selection));
        return newState;
      });
    } else {
      // If selection is cleared, remove all business entity filters
      setQueryParams(prevState => {
        const newState = new Map(prevState);
        BUSINESS_UNIT_QUERY_PARAMS.forEach(param => newState.delete(param));
        return newState;
      });
    }
  }, []);

  const handleDateRangeSelection = useCallback((startDate: Date | null, endDate: Date | null) => {
    setQueryParams(prevState => {
      const newState = new Map(prevState);
      if (startDate) {
        newState.set('start_date', startDate.toISOString());
      } else {
        newState.delete('start_date');
      }
      if (endDate) {
        newState.set('end_date', endDate.toISOString());
      } else {
        newState.delete('end_date');
      }
      return newState;
    });
  }, []);

  const handleProductSelection = useCallback((selection: ProductSelection) => {
    setQueryParams(prevState => {
      const newState = new Map(prevState ?? []);
      if (selection) {
        newState.set('product', selection);
      } else {
        newState.set('product', ALL_PRODUCTS);
      }
      return newState;
    });
  }, []);

  const scrollToTop = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [tableRef]);

  const refreshLogs = useCallback(
    async (queryParams?: QueryParams) => {
      setCurrentPage(1);
      setHasNextPage(true);
      await loadLogsPage({ page: 1, append: false, queryParams });
      scrollToTop();
    },
    [loadLogsPage, scrollToTop]
  );

  const loadMore = useCallback(async () => {
    if (!hasNextPage || isRequesting) {
      return;
    }

    setIsRequesting(true);
    try {
      await loadLogsPage({ page: currentPage + 1, append: true });
    } finally {
      setIsRequesting(false);
    }
  }, [hasNextPage, isRequesting, currentPage, loadLogsPage]);

  useEffect(() => {
    if (userId) {
      getLogsScope();
    }
  }, [userId, getLogsScope]);

  useEffect(() => {
    if (product !== undefined && logsScope) {
      setQueryParams(prevState => {
        const newState = new Map(prevState);
        if (product) {
          newState.set('product', product);
        } else {
          newState.set('product', ALL_PRODUCTS);
        }
        return newState;
      });
    }
  }, [product, logsScope]);

  useEffect(() => {
    // queryParams state is null when we haven't manually made any filtering decision
    if (queryParams !== null) {
      refreshLogs(queryParams);
    }
  }, [queryParams, refreshLogs]);

  return {
    logsScope,
    logs,
    refreshLogs,
    loadMore,
    isRequesting,
    hasNextPage,
    hasAccess,
    handleBusinessEntitySelection,
    handleDateRangeSelection,
    handleProductSelection,
    queryParams,
    productSelection: (queryParams?.get('product') as ProductSelection | undefined) ?? ALL_PRODUCTS,
  };
};

export default useAuditLogs;
